import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query";
import { toast } from "sonner";

import { AccountActivityProcessingStatusDto, AccountDto } from "@/types";
import { showErrorToast } from "@/lib/error-handling";
import {
  fetchAccounts,
  createAccount,
  fetchAccountById,
  fetchActivityProcessingStatus,
  updateAccount,
  UpdateAccountRequest,
  prepareAccountData,
  AccountFormData,
} from "@/services/frontendServices/accountsServices";

// Keys for queries, to maintain consistency
export const accountKeys = {
  all: ["accounts"] as const,
  lists: () => [...accountKeys.all, "list"] as const,
  list: (filters: never) => [...accountKeys.lists(), { filters }] as const,
  details: () => [...accountKeys.all, "detail"] as const,
  detail: (accountId: string) => [...accountKeys.details(), accountId] as const,
  processingStatus: (accountId: string) =>
    [...accountKeys.detail(accountId), "activity-processing-status"] as const,
};

// Hook to get all accounts
export const useAccounts = (
  options?: Omit<UseQueryOptions<AccountDto[], Error>, "queryKey" | "queryFn">
) => {
  return useQuery<AccountDto[], Error>({
    queryKey: accountKeys.lists(),
    queryFn: () => fetchAccounts(),
    ...options,
  });
};

// Hook to get a specific account
export const useAccount = (accountId: string) => {
  return useQuery({
    queryKey: accountKeys.detail(accountId),
    queryFn: () => fetchAccountById(accountId),
    enabled: !!accountId, // It only runs if we have an ID
  });
};

// Hook to get activity processing status for an account
export const useActivityProcessingStatus = (accountId: string) => {
  return useQuery<AccountActivityProcessingStatusDto, Error>({
    queryKey: accountKeys.processingStatus(accountId),
    queryFn: () => fetchActivityProcessingStatus(accountId),
    enabled: !!accountId,
    retry: 0,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    staleTime: 0,
  });
};

// Hook to create new account
export const useCreateAccount = () => {
  const queryClient = useQueryClient();

  return useMutation<AccountDto, Error, AccountFormData>({
    mutationFn: async (formData: AccountFormData) => {
      const apiData = prepareAccountData(formData);
      const newAccount = await createAccount(apiData);
      return newAccount;
    },
    onSuccess: (newAccount) => {
      // Invalidates the account list query to force a refetch
      queryClient.invalidateQueries({ queryKey: accountKeys.lists() });

      toast.success("Account created successfully!", {
        description: `Account "${newAccount.name}" has been created.`,
        duration: 3000,
      });
    },
    onError: (error) => {
      showErrorToast(error, {
        title: "Error creating account",
        defaultMessage: "Failed to create account",
        duration: 5000,
      });
    },
  });
};

export const useUpdateAccount = () => {
  const queryClient = useQueryClient();

  return useMutation<
    AccountDto,
    Error,
    { accountId: string; formData: UpdateAccountRequest }
  >({
    mutationFn: ({ accountId, formData }) => updateAccount(accountId, formData),
    onSuccess: (updatedAccount) => {
      // Update the individual account detail
      queryClient.setQueryData<AccountDto>(
        accountKeys.detail(updatedAccount.id),
        updatedAccount
      );

      // Invalidate related queries if necessary
      queryClient.invalidateQueries({
        queryKey: accountKeys.detail(updatedAccount.id),
      });

      // Invalidate the account list to reflect changes
      queryClient.invalidateQueries({ queryKey: accountKeys.lists() });

      toast.success("Account updated successfully!", {
        description: `Account has been updated.`,
        duration: 3000,
      });
    },
    onError: (error) => {
      showErrorToast(error, {
        title: "Error updating account",
        defaultMessage: "Failed to update account",
        duration: 5000,
      });
    },
  });
};
